import { DEFAULT_STATUS_MESSAGES, ORDER_STATUS_LABELS, ORDER_STATUSES } from '@centerpanos/types';
import { Button } from '@centerpanos/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { PageHeader } from '../components/layout';
import { Card, Field, Input, Spinner, Switch, Textarea } from '../components/ui';
import { api, type BusinessHourDto } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useToast } from '../lib/toast';

const WEEKDAYS = [
  'Domingo',
  'Segunda-feira',
  'Terça-feira',
  'Quarta-feira',
  'Quinta-feira',
  'Sexta-feira',
  'Sábado',
];

interface StoreForm {
  name: string;
  whatsapp: string;
  street: string;
  number: string;
  district: string;
  city: string;
  whatsappBotaoMensagem: string;
  msgFeriado: string;
  feriadoAtivo: boolean;
}

// Campos exigidos pelo schema mas não editáveis neste painel — preservados do
// valor salvo para não quebrar a validação da API.
interface StoreHidden {
  legalName: string;
  cnpj: string;
  phone: string | null;
  email: string | null;
  uf: string;
  cep: string;
  instagram: string | null;
  facebook: string | null;
}

export function SettingsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [store, setStore] = useState<StoreForm | null>(null);
  const [hidden, setHidden] = useState<StoreHidden>({
    legalName: '',
    cnpj: '',
    phone: null,
    email: null,
    uf: 'SP',
    cep: '',
    instagram: null,
    facebook: null,
  });
  const [hours, setHours] = useState<BusinessHourDto[]>([]);
  const [mensagens, setMensagens] = useState<Record<string, string>>({});
  const [msgsExpandido, setMsgsExpandido] = useState(false);

  const settings = useQuery({ queryKey: ['configuracoes'], queryFn: api.settings.get });

  useEffect(() => {
    if (!settings.data) return;

    const info = settings.data.info;
    setStore({
      name: info?.name ?? '',
      whatsapp: info?.whatsapp ?? '',
      street: info?.address.street ?? '',
      number: info?.address.number ?? '',
      district: info?.address.district ?? '',
      city: info?.address.city ?? '',
      whatsappBotaoMensagem: info?.whatsappBotao?.mensagem ?? '',
      msgFeriado: info?.msgFeriado ?? '',
      feriadoAtivo: info?.feriadoAtivo ?? false,
    });

    setMensagens({ ...DEFAULT_STATUS_MESSAGES, ...(info?.mensagensStatus ?? {}) });

    setHidden({
      legalName: info?.legalName ?? '',
      cnpj: info?.cnpj ?? '',
      phone: info?.phone ?? null,
      email: info?.email ?? null,
      uf: info?.address.uf ?? 'SP',
      cep: info?.address.cep ?? '',
      instagram: info?.instagram ?? null,
      facebook: info?.facebook ?? null,
    });

    setHours(
      settings.data.hours.length === 7
        ? settings.data.hours
        : WEEKDAYS.map((_, weekday) => ({
            weekday,
            opensAt: '09:00',
            closesAt: '18:00',
            closed: weekday === 0,
          })),
    );
  }, [settings.data]);

  const saveStore = useMutation({
    mutationFn: (form: StoreForm) =>
      api.settings.updateStore({
        name: form.name,
        legalName: hidden.legalName,
        cnpj: hidden.cnpj,
        whatsapp: form.whatsapp.replace(/\D/g, ''),
        phone: hidden.phone,
        email: hidden.email,
        address: {
          street: form.street,
          number: form.number,
          district: form.district,
          city: form.city,
          uf: hidden.uf,
          cep: hidden.cep,
        },
        instagram: hidden.instagram,
        facebook: hidden.facebook,
        whatsappBotao: {
          ativo: true,
          mensagem: form.whatsappBotaoMensagem,
        },
        msgFeriado: form.msgFeriado || null,
        feriadoAtivo: form.feriadoAtivo,
        mensagensStatus: mensagens,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['configuracoes'] });
      toast.success('Dados da loja salvos.');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const [novoFeriado, setNovoFeriado] = useState({ date: '', label: '' });

  const addHoliday = useMutation({
    mutationFn: () => api.settings.addHoliday(novoFeriado.date, novoFeriado.label),
    onSuccess: async () => {
      setNovoFeriado({ date: '', label: '' });
      await queryClient.invalidateQueries({ queryKey: ['configuracoes'] });
      toast.success('Feriado adicionado.');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const removeHoliday = useMutation({
    mutationFn: (date: string) => api.settings.removeHoliday(date),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['configuracoes'] });
      toast.success('Feriado removido.');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const saveHours = useMutation({
    mutationFn: () => api.settings.updateHours(hours),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['configuracoes'] });
      toast.success('Horários salvos. O aviso de aberto/fechado já usa os novos.');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (settings.isLoading || !store) return <Spinner />;

  const readOnly = !can('configuracoes:editar');

  return (
    <>
      <PageHeader
        title="Configurações"
        description="Dados que aparecem na LP, no WhatsApp e no aviso de horário."
      />

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="space-y-5">
          <Card
            title="Dados da loja"
            actions={
              <Button
                size="sm"
                disabled={readOnly || saveStore.isPending}
                onClick={() => saveStore.mutate(store)}
              >
                Salvar
              </Button>
            }
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Nome fantasia" required className="sm:col-span-2">
                <Input
                  value={store.name}
                  onChange={(event) => setStore({ ...store, name: event.target.value })}
                  disabled={readOnly}
                />
              </Field>

              <Field label="WhatsApp" hint="Com DDI e DDD: 5511911328999" className="sm:col-span-2">
                <Input
                  value={store.whatsapp}
                  onChange={(event) => setStore({ ...store, whatsapp: event.target.value })}
                  disabled={readOnly}
                />
              </Field>

              <Field label="Rua" className="sm:col-span-2">
                <Input
                  value={store.street}
                  onChange={(event) => setStore({ ...store, street: event.target.value })}
                  disabled={readOnly}
                />
              </Field>

              <Field label="Número">
                <Input
                  value={store.number}
                  onChange={(event) => setStore({ ...store, number: event.target.value })}
                  disabled={readOnly}
                />
              </Field>

              <Field label="Bairro">
                <Input
                  value={store.district}
                  onChange={(event) => setStore({ ...store, district: event.target.value })}
                  disabled={readOnly}
                />
              </Field>

              <Field label="Cidade" className="sm:col-span-2">
                <Input
                  value={store.city}
                  onChange={(event) => setStore({ ...store, city: event.target.value })}
                  disabled={readOnly}
                />
              </Field>
            </div>
          </Card>

          <Card
            title="Mensagem do WhatsApp"
            description="Texto pré-preenchido ao clicar em qualquer botão de WhatsApp do site."
            actions={
              <Button
                size="sm"
                disabled={readOnly || saveStore.isPending}
                onClick={() => saveStore.mutate(store)}
              >
                Salvar
              </Button>
            }
          >
            <Field label="" hint="Deixe em branco para abrir sem mensagem.">
              <Input
                value={store.whatsappBotaoMensagem}
                placeholder="Olá! Gostaria de saber mais sobre os produtos."
                onChange={(event) =>
                  setStore({ ...store, whatsappBotaoMensagem: event.target.value })
                }
                disabled={readOnly}
              />
            </Field>
          </Card>

          <Card
            title="Mensagens por status"
            description="Texto enviado ao cliente no WhatsApp em cada status do pedido. Variáveis: {nome} {codigo} {valor} {loja} {pix}."
            actions={
              <Button
                size="sm"
                disabled={readOnly || saveStore.isPending}
                onClick={() => saveStore.mutate(store)}
              >
                Salvar
              </Button>
            }
          >
            <div className="space-y-4">
              {ORDER_STATUSES.filter((status) => msgsExpandido || status === 'novo').map(
                (status) => (
                  <Field key={status} label={ORDER_STATUS_LABELS[status]}>
                    <Textarea
                      rows={3}
                      value={mensagens[status] ?? ''}
                      onChange={(event) =>
                        setMensagens((prev) => ({ ...prev, [status]: event.target.value }))
                      }
                      disabled={readOnly}
                    />
                  </Field>
                ),
              )}

              <button
                type="button"
                onClick={() => setMsgsExpandido((v) => !v)}
                className="text-brand-700 text-sm font-semibold hover:underline"
              >
                {msgsExpandido ? 'Ocultar' : `Mostrar todos os status (${ORDER_STATUSES.length})`}
              </button>
            </div>
          </Card>
        </div>

        <div className="space-y-5">
          <Card
            title="Horário de funcionamento"
            description='Define o aviso "Aberto agora / Fechado" no site.'
            actions={
              <Button
                size="sm"
                disabled={readOnly || saveHours.isPending}
                onClick={() => saveHours.mutate()}
              >
                Salvar
              </Button>
            }
          >
            <ul className="space-y-3">
              {hours.map((hour, index) => (
                <li key={hour.weekday} className="flex flex-wrap items-center gap-3">
                  <span className="text-ink-700 w-28 text-sm">{WEEKDAYS[hour.weekday]}</span>

                  <Input
                    type="time"
                    aria-label={`Abre em ${WEEKDAYS[hour.weekday]}`}
                    className="w-28"
                    value={hour.opensAt}
                    disabled={hour.closed || readOnly}
                    onChange={(event) =>
                      setHours((current) =>
                        current.map((item, i) =>
                          i === index ? { ...item, opensAt: event.target.value } : item,
                        ),
                      )
                    }
                  />

                  <Input
                    type="time"
                    aria-label={`Fecha em ${WEEKDAYS[hour.weekday]}`}
                    className="w-28"
                    value={hour.closesAt}
                    disabled={hour.closed || readOnly}
                    onChange={(event) =>
                      setHours((current) =>
                        current.map((item, i) =>
                          i === index ? { ...item, closesAt: event.target.value } : item,
                        ),
                      )
                    }
                  />

                  <Switch
                    label="Fechado"
                    checked={hour.closed}
                    onChange={(closed) => {
                      if (readOnly) return;
                      setHours((current) =>
                        current.map((item, i) => (i === index ? { ...item, closed } : item)),
                      );
                    }}
                  />
                </li>
              ))}
            </ul>
          </Card>

          <Card
            title="Aviso de feriado"
            description="Exibido quando hoje for feriado nacional ou cadastrado abaixo."
            actions={
              <Button
                size="sm"
                disabled={readOnly || saveStore.isPending}
                onClick={() => saveStore.mutate(store)}
              >
                Salvar
              </Button>
            }
          >
            <div className="space-y-4">
              <Switch
                label="Ativar aviso de feriado"
                checked={store.feriadoAtivo}
                onChange={(v) =>
                  setStore({
                    ...store,
                    feriadoAtivo: v,
                    msgFeriado:
                      v && !store.msgFeriado
                        ? 'Hoje é feriado. O horário pode ser diferente — confirme pelo WhatsApp.'
                        : store.msgFeriado,
                  })
                }
              />

              {store.feriadoAtivo && (
                <Field
                  label="Mensagem"
                  hint="Aparece em feriados nacionais e nos cadastrados abaixo."
                >
                  <Input
                    value={store.msgFeriado}
                    placeholder="Hoje é feriado. O horário pode ser diferente — confirme pelo WhatsApp."
                    onChange={(event) => setStore({ ...store, msgFeriado: event.target.value })}
                    disabled={readOnly}
                  />
                </Field>
              )}
            </div>
          </Card>

          <Card
            title="Feriados cadastrados"
            description="Complementam os feriados nacionais calculados automaticamente."
          >
            <div className="space-y-4">
              {(settings.data?.holidays ?? []).length === 0 ? (
                <p className="text-ink-400 text-sm">Nenhum feriado cadastrado.</p>
              ) : (
                <ul className="divide-ink-100 divide-y text-sm">
                  {(settings.data?.holidays ?? []).map((h) => (
                    <li key={h.date} className="flex items-center justify-between gap-3 py-2">
                      <span className="text-ink-700">
                        <span className="font-medium">{h.date}</span>
                        {' — '}
                        {h.label}
                      </span>
                      {!readOnly && (
                        <button
                          type="button"
                          className="text-xs text-red-600 hover:underline disabled:opacity-40"
                          disabled={removeHoliday.isPending}
                          onClick={() => removeHoliday.mutate(h.date)}
                        >
                          Remover
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {!readOnly && (
                <form
                  className="flex flex-wrap gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (novoFeriado.date && novoFeriado.label) addHoliday.mutate();
                  }}
                >
                  <Input
                    type="date"
                    value={novoFeriado.date}
                    onChange={(e) => setNovoFeriado({ ...novoFeriado, date: e.target.value })}
                    className="w-40"
                    aria-label="Data do feriado"
                  />
                  <Input
                    value={novoFeriado.label}
                    onChange={(e) => setNovoFeriado({ ...novoFeriado, label: e.target.value })}
                    placeholder="Ex.: Aniversário de Guarulhos"
                    className="flex-1"
                    aria-label="Nome do feriado"
                  />
                  <Button
                    type="submit"
                    size="sm"
                    variant="outline"
                    disabled={!novoFeriado.date || !novoFeriado.label || addHoliday.isPending}
                  >
                    Adicionar
                  </Button>
                </form>
              )}
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
